// Payroll - Allowances & Deductions Tab (With Force, Group, and Individual Scoping)
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';
import PersonnelSelect from './PersonnelSelect';

const STATUS_COLORS = {
  pending:  { bg: '#fef9c3', text: '#854d0e', label: 'Pending' },
  approved: { bg: '#dcfce7', text: '#166534', label: 'Approved' },
  rejected: { bg: '#fee2e2', text: '#991b1b', label: 'Rejected' },
};

const Badge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.text }}>{s.label}</span>;
};

const PaginationControls = ({ currentPage, totalItems, pageSize, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0', fontSize: 12 }}>
      <div style={{ color: '#64748b', fontWeight: 600 }}>
        Showing <strong>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalItems)}</strong> of <strong>{totalItems}</strong> items
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
            background: currentPage === 1 ? '#f1f5f9' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#334155',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 700
          }}
        >
          ◀ Prev
        </button>
        <span style={{ fontWeight: 700, color: '#334155', padding: '0 6px' }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1',
            background: currentPage === totalPages ? '#f1f5f9' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#334155',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: 700
          }}
        >
          Next ▶
        </button>
      </div>
    </div>
  );
};

const AllowancesTab = ({ api, user, armyTypeId, groups }) => {
  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [section, setSection] = useState('allowances');

  // Form States
  const [form, setForm] = useState({
    name: '', allowance_type: 'G_Xil', amount_type: 'fixed', amount: '',
    scope_type: 'force', payroll_group_id: '', army_personnel_id: '', is_recurring: true
  });
  const [dedForm, setDedForm] = useState({
    name: '', deduction_type: 'MM', amount_type: 'fixed', amount: '',
    scope_type: 'force', payroll_group_id: '', army_personnel_id: ''
  });

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const canApprove = hasAnyRole(user, ['admin', 'finance_officer', 'finance_director']);
  const canCreate = hasAnyRole(user, ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance']);

  const [allowancePage, setAllowancePage] = useState(1);
  const [deductionPage, setDeductionPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    setAllowancePage(1);
    setDeductionPage(1);
    fetchData();
  }, [armyTypeId]);

  const fetchData = async () => {
    if (!armyTypeId) return;
    try {
      const aRes = await api.get(`/allowances?army_type_id=${armyTypeId}`);
      const aList = aRes?.allowances || aRes?.data?.allowances || [];
      setAllowances(aList);
    } catch (e) { console.error('Error fetching allowances:', e); }

    try {
      const dRes = await api.get(`/deductions?army_type_id=${armyTypeId}`);
      const dList = dRes?.deductions || dRes?.data?.deductions || [];
      setDeductions(dList);
    } catch (e) { console.error('Error fetching deductions:', e); }

    try {
      const pRes = await api.get(`/army_personnel?army_type_id=${armyTypeId}`);
      const pList = pRes?.army_personnel || pRes?.data?.army_personnel || [];
      setPersonnelList(pList);
    } catch (e) { console.error('Error fetching personnel:', e); }
  };

  const handleApprove = async (id, type) => {
    try {
      const res = await api.patch(`/${type}/${id}/approve`);
      if (res?.response && !res.response.ok) {
        setMsg(res.data?.error || 'Error approving');
      } else {
        setMsg(`✅ ${type === 'allowances' ? 'Allowance' : 'Deduction'} approved successfully`);
        fetchData();
      }
    } catch (e) { setMsg('Error approving'); }
  };

  const handleReject = async (id, type) => {
    if (!confirm('Reject this item?')) return;
    try {
      const res = await api.patch(`/${type}/${id}/reject`);
      if (res?.response && !res.response.ok) {
        setMsg(res.data?.error || 'Error rejecting');
      } else {
        setMsg(`❌ ${type === 'allowances' ? 'Allowance' : 'Deduction'} rejected`);
        fetchData();
      }
    } catch (e) { setMsg('Error rejecting'); }
  };

  const handleDelete = async (id, type) => {
    if (!confirm('Delete this item?')) return;
    try {
      const res = await api.delete(`/${type}/${id}`);
      if (res?.response && !res.response.ok) {
        alert(res.data?.error || 'Cannot delete item');
      } else {
        fetchData();
      }
    } catch (e) { alert('Cannot delete approved item'); }
  };

  const parseErrorMsg = (data, defaultMsg) => {
    if (!data) return defaultMsg;
    if (typeof data.error === 'string') return data.error;
    if (data.errors) {
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      if (typeof data.errors === 'object') {
        return Object.entries(data.errors)
          .map(([k, v]) => `${k.replace('_', ' ')} ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
      }
    }
    return defaultMsg;
  };

  const submitAllowance = async (e) => {
    e.preventDefault(); setLoading(true); setMsg('');
    try {
      const payload = {
        allowance: {
          name: form.name,
          allowance_type: form.allowance_type,
          amount_type: form.amount_type,
          amount: form.amount,
          is_recurring: form.is_recurring,
          army_type_id: armyTypeId,
          payroll_group_id: form.scope_type === 'group' ? form.payroll_group_id : null,
          army_personnel_id: form.scope_type === 'individual' ? form.army_personnel_id : null
        }
      };
      const res = await api.post('/allowances', payload);
      if (res?.response && !res.response.ok) {
        setMsg(`❌ ${parseErrorMsg(res.data, 'Error creating allowance')}`);
      } else {
        setMsg('✅ Allowance created and submitted for Finance approval');
        setForm({
          name: '', allowance_type: 'G_Xil', amount_type: 'fixed', amount: '',
          scope_type: 'force', payroll_group_id: '', army_personnel_id: '', is_recurring: true
        });
        setAllowancePage(1);
        await fetchData();
      }
    } catch (e) { setMsg('❌ Error creating allowance'); }
    finally { setLoading(false); }
  };

  const submitDeduction = async (e) => {
    e.preventDefault(); setLoading(true); setMsg('');
    try {
      const payload = {
        deduction: {
          name: dedForm.name,
          deduction_type: dedForm.deduction_type,
          amount_type: dedForm.amount_type,
          amount: dedForm.amount,
          army_type_id: armyTypeId,
          payroll_group_id: dedForm.scope_type === 'group' ? dedForm.payroll_group_id : null,
          army_personnel_id: dedForm.scope_type === 'individual' ? dedForm.army_personnel_id : null
        }
      };
      const res = await api.post('/deductions', payload);
      if (res?.response && !res.response.ok) {
        setMsg(`❌ ${parseErrorMsg(res.data, 'Error creating deduction')}`);
      } else {
        setMsg('✅ Deduction created and submitted for Finance approval');
        setDedForm({
          name: '', deduction_type: 'MM', amount_type: 'fixed', amount: '',
          scope_type: 'force', payroll_group_id: '', army_personnel_id: ''
        });
        setDeductionPage(1);
        await fetchData();
      }
    } catch (e) { setMsg('❌ Error creating deduction'); }
    finally { setLoading(false); }
  };

  const tableHeader = (cols) => (
    <thead>
      <tr style={{ background: '#f8fafc' }}>
        {cols.map(c => <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{c}</th>)}
      </tr>
    </thead>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['allowances','deductions'].map(s => (
          <button key={s} onClick={() => setSection(s)}
            style={{ padding: '8px 20px', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', border: 'none',
              background: section === s ? '#1b4332' : '#f1f5f9', color: section === s ? '#fff' : '#374151' }}>
            {s === 'allowances' ? '💰 Allowances & Bonuses' : '📉 Deductions'}
          </button>
        ))}
      </div>

      {msg && <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13, fontWeight: 600 }}>{msg}</div>}

      {/* SECTION 1: ALLOWANCES */}
      {section === 'allowances' && (
        <>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
            <strong>Hierarchical Scoping:</strong> Individual &gt; Payroll Group &gt; Force Level. Individual settings automatically override group and force defaults during payroll calculation.
          </div>

          {canCreate && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Add Allowance / Bonus</h3>
              <form onSubmit={submitAllowance} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name *</label>
                  <input type="text" required value={form.name} placeholder="e.g. Technical Bonus"
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                </div>

                <div style={{ flex: '1 1 130px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
                  <select value={form.allowance_type} onChange={e => setForm({ ...form, allowance_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="G_Xil">Allowance (G_Xil)</option>
                    <option value="G_Farsamo">Bonus (G_Farsamo)</option>
                    <option value="G_Hawl">Service (G_Hawl)</option>
                  </select>
                </div>

                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount Type</label>
                  <select value={form.amount_type} onChange={e => setForm({ ...form, amount_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="fixed">Fixed ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>

                <div style={{ flex: '1 1 100px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount *</label>
                  <input type="number" step="0.01" required value={form.amount} placeholder="0.00"
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                </div>

                {/* Scope Selection */}
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Scope Level</label>
                  <select value={form.scope_type} onChange={e => setForm({ ...form, scope_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="force">Force Wide</option>
                    <option value="group">Payroll Group</option>
                    <option value="individual">Individual Personnel</option>
                  </select>
                </div>

                {form.scope_type === 'group' && (
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Select Group *</label>
                    <select required value={form.payroll_group_id} onChange={e => setForm({ ...form, payroll_group_id: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                      <option value="">Select Group</option>
                      {(groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}

                {form.scope_type === 'individual' && (
                  <div style={{ flex: '1 1 280px' }}>
                    <PersonnelSelect
                      personnelList={personnelList}
                      value={form.army_personnel_id}
                      required={true}
                      onChange={(pId) => setForm({ ...form, army_personnel_id: pId })}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <input type="checkbox" id="recurring" checked={form.is_recurring}
                    onChange={e => setForm({ ...form, is_recurring: e.target.checked })} />
                  <label htmlFor="recurring" style={{ fontSize: 12, fontWeight: 600 }}>Recurring</label>
                </div>

                <button type="submit" disabled={loading}
                  style={{ padding: '9px 20px', background: '#1b4332', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  {loading ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </form>
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', paddingBottom: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              {tableHeader(['Name', 'Type', 'Amount', 'Target Scope', 'Recurring', 'Status', 'Actions'])}
              <tbody>
                {allowances.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No allowances found for this force.</td></tr>}
                {allowances.slice((allowancePage - 1) * PAGE_SIZE, allowancePage * PAGE_SIZE).map((a, i) => {
                  let scopeLabel = 'Entire Force';
                  if (a.payroll_group) {
                    scopeLabel = `Group: ${a.payroll_group.name}`;
                  } else if (a.army_personnel) {
                    const pName = a.army_personnel.full_name || `${a.army_personnel.first_name || ''} ${a.army_personnel.last_name || ''}`.trim() || a.army_personnel.army_id || `ID #${a.army_personnel.id}`;
                    scopeLabel = `Individual: ${pName}`;
                  }

                  return (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{a.name}</td>
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{a.allowance_type}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#059669' }}>
                        {a.amount_type === 'percentage' ? `${a.amount}%` : `$${parseFloat(a.amount).toFixed(2)}`}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>{scopeLabel}</td>
                      <td style={{ padding: '10px 14px' }}>{a.is_recurring ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '10px 14px' }}><Badge status={a.status} /></td>
                      <td style={{ padding: '10px 14px' }}>
                        {canApprove && a.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(a.id, 'allowances')}
                              style={{ marginRight: 4, padding: '3px 10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Approve</button>
                            <button onClick={() => handleReject(a.id, 'allowances')}
                              style={{ marginRight: 4, padding: '3px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Reject</button>
                          </>
                        )}
                        {(a.status === 'pending' || canApprove) && (
                          <button onClick={() => handleDelete(a.id, 'allowances')}
                            style={{ padding: '3px 10px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Delete</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div style={{ padding: '0 16px' }}>
              <PaginationControls
                currentPage={allowancePage}
                totalItems={allowances.length}
                pageSize={PAGE_SIZE}
                onPageChange={setAllowancePage}
              />
            </div>
          </div>
        </>
      )}

      {/* SECTION 2: DEDUCTIONS */}
      {section === 'deductions' && (
        <>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, color: '#991b1b' }}>
            <strong>Deduction Workflow & Scope:</strong> Supports individual & group scoping (MM, Bolo, Insurance). Deductions must be approved by Finance before being applied to payroll runs.
          </div>

          {canCreate && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Add Deduction</h3>
              <form onSubmit={submitDeduction} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name *</label>
                  <input required value={dedForm.name} placeholder="e.g. Insurance Plan"
                    onChange={e => setDedForm({ ...dedForm, name: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                </div>

                <div style={{ flex: '1 1 110px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
                  <select value={dedForm.deduction_type} onChange={e => setDedForm({ ...dedForm, deduction_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="MM">MM</option>
                    <option value="Bolo">Bolo</option>
                    <option value="Insurance">Insurance</option>
                  </select>
                </div>

                <div style={{ flex: '1 1 110px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount Type</label>
                  <select value={dedForm.amount_type} onChange={e => setDedForm({ ...dedForm, amount_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="fixed">Fixed ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>

                <div style={{ flex: '1 1 100px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount *</label>
                  <input type="number" step="0.01" required value={dedForm.amount} min="0" placeholder="0.00"
                    onChange={e => setDedForm({ ...dedForm, amount: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
                </div>

                {/* Scope Selection */}
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Scope Level</label>
                  <select value={dedForm.scope_type} onChange={e => setDedForm({ ...dedForm, scope_type: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                    <option value="force">Force Wide</option>
                    <option value="group">Payroll Group</option>
                    <option value="individual">Individual Personnel</option>
                  </select>
                </div>

                {dedForm.scope_type === 'group' && (
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Select Group *</label>
                    <select required value={dedForm.payroll_group_id} onChange={e => setDedForm({ ...dedForm, payroll_group_id: e.target.value })}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                      <option value="">Select Group</option>
                      {(groups || []).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                )}

                {dedForm.scope_type === 'individual' && (
                  <div style={{ flex: '1 1 280px' }}>
                    <PersonnelSelect
                      personnelList={personnelList}
                      value={dedForm.army_personnel_id}
                      required={true}
                      onChange={(pId) => setDedForm({ ...dedForm, army_personnel_id: pId })}
                    />
                  </div>
                )}

                <button type="submit" disabled={loading}
                  style={{ padding: '9px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  {loading ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </form>
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', paddingBottom: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              {tableHeader(['Name', 'Type', 'Amount', 'Target Scope', 'Status', 'Actions'])}
              <tbody>
                {deductions.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No deductions found.</td></tr>}
                {deductions.slice((deductionPage - 1) * PAGE_SIZE, deductionPage * PAGE_SIZE).map((d, i) => {
                  let scopeLabel = 'Entire Force';
                  if (d.payroll_group) {
                    scopeLabel = `Group: ${d.payroll_group.name}`;
                  } else if (d.army_personnel) {
                    const pName = d.army_personnel.full_name || `${d.army_personnel.first_name || ''} ${d.army_personnel.last_name || ''}`.trim() || d.army_personnel.army_id || `ID #${d.army_personnel.id}`;
                    scopeLabel = `Individual: ${pName}`;
                  }

                  return (
                    <tr key={d.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{d.name}</td>
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{d.deduction_type}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626' }}>
                        {d.amount_type === 'percentage' ? `${d.amount}%` : `$${parseFloat(d.amount).toFixed(2)}`}
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: '#374151' }}>{scopeLabel}</td>
                      <td style={{ padding: '10px 14px' }}><Badge status={d.status} /></td>
                      <td style={{ padding: '10px 14px' }}>
                        {canApprove && d.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(d.id, 'deductions')}
                              style={{ marginRight: 4, padding: '3px 10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Approve</button>
                            <button onClick={() => handleReject(d.id, 'deductions')}
                              style={{ marginRight: 4, padding: '3px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Reject</button>
                          </>
                        )}
                        {(d.status === 'pending' || canApprove) && (
                          <button onClick={() => handleDelete(d.id, 'deductions')}
                            style={{ padding: '3px 10px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Delete</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div style={{ padding: '0 16px' }}>
              <PaginationControls
                currentPage={deductionPage}
                totalItems={deductions.length}
                pageSize={PAGE_SIZE}
                onPageChange={setDeductionPage}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllowancesTab;
